import IdGenerator from 'scripts/domain/contracts/id_generator';

import { InfrastructureError } from 'scripts/application/lib/error';
import { handleCommonErrors } from 'scripts/application/lib/gateway_error_silent_handler';
import { clearLoadingSafetyTimer, startLoadingSafetyTimer } from 'actions/team_assist/lib/loading_safety_timer';
import TeamAssistMessage from 'models/team_assist_message';

/**
 * TriggerTeamAssistFollowUp fires the post-execute follow-up assist
 * request after the agent has executed a previously-proposed destructive
 * tool. It is NOT user-initiated — it is auto-dispatched by
 * ExecuteTeamAssistProposedAction once the destructive call resolves,
 * carrying `{ messageId, toolName, isError, result }` so the LLM can
 * interpret the outcome and emit a `respond_to_agent` bubble.
 *
 * The optimistic message is a `system`-role `follow_up_trigger` — the
 * persisted anchor on the server. It is filtered out of the visible
 * bubble stream client-side; the visible signal to the agent is the
 * proposed-action card's post-execute state plus the LLM's eventual
 * follow-up bubble that arrives via MQTT broadcast.
 *
 * This action is intentionally separate from AskTeamAssist so the two
 * flows have distinct call sites, distinct analytics, distinct
 * optimistic messages, and distinct test surfaces.
 */
export default class TriggerTeamAssistFollowUp {
  constructor(context) {
    this.context = context;
  }

  run({ customerId, proposedActionResults }) {
    if (!customerId) {
      return;
    }
    if (!Array.isArray(proposedActionResults) || proposedActionResults.length === 0) {
      return;
    }

    if (!this.context.stores.customers.has({ id: customerId })) {
      return;
    }

    const { teamAssists } = this.context.stores.customers.storesFor(customerId);

    const messageId = IdGenerator.newId();
    const optimisticMessage = TeamAssistMessage.create({
      createdAt: new Date().toISOString(),
      customerId,
      id: messageId,
      proposedActionResults,
      role: TeamAssistMessage.Role.system,
      type: TeamAssistMessage.Type.follow_up_trigger,
    });
    teamAssists.addOrReplace(optimisticMessage);
    teamAssists.setLoading();
    startLoadingSafetyTimer(customerId, teamAssists);

    const body = { id: messageId, proposedActionResults };

    // Supernova may handle this fire-and-forget (response returns only
    // the trigger; the LLM's interpretation arrives via MQTT broadcast)
    // or synchronously (response carries the AI messages inline). Both
    // shapes must be tolerated.
    this.context.gateways.teamAssist
      .add({ customerId }, body)
      .then(response => {
        const aiMessages = (response && response.messages ? response.messages : []).filter(
          m => m && m.role === TeamAssistMessage.Role.ai_response
        );
        if (aiMessages.length === 0) {
          return;
        }
        aiMessages.forEach(m => teamAssists.addOrReplace(TeamAssistMessage.fromJs(m)));
        teamAssists.resetLoading();
        clearLoadingSafetyTimer(customerId);
      })
      .catch(err => {
        // Infrastructure errors (timeouts, network blips) don't mean the
        // follow-up failed — supernova may still complete and stream the
        // interpretation via broadcast. Only treat definite rejections
        // (validation, permission, etc.) as a final failure, in which
        // case we mark the trigger message with `error: true` so the
        // panel can render a failed state and clear the loading bubble.
        if (!(err instanceof InfrastructureError)) {
          const msg = teamAssists.find(messageId);
          if (msg) {
            teamAssists.addOrReplace(TeamAssistMessage.fromJs({ ...msg.toJs(), error: true }));
          }
          teamAssists.resetLoading();
          clearLoadingSafetyTimer(customerId);
        }
        handleCommonErrors(this.context, err);
      });
  }
}
